import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, History, Rocket, Shield, Layout, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Markdown from 'react-markdown';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import changelogRaw from '../../CHANGELOG.md?raw';

export default function Changelog() {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      const SUPER_ADMIN_EMAILS = ['holasolonet@gmail.com', 'cinside.info@gmail.com'];
      if (SUPER_ADMIN_EMAILS.includes(user.email || '')) {
        setIsAuthorized(true);
        setContent(changelogRaw);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.role === 'admin' || userData.role === 'sales') {
            if (userData.canAccessCRM) {
              setIsAuthorized(true);
              setContent(changelogRaw);
            } else {
              setIsAuthorized(false);
              setTimeout(() => navigate('/admin'), 2000);
            }
          } else {
            setIsAuthorized(false);
            setTimeout(() => navigate('/admin'), 2000);
          }
        } else {
          setIsAuthorized(false);
          setTimeout(() => navigate('/admin'), 2000);
        }
      } catch (error) {
        console.error("Error authorizing changelog:", error);
        setIsAuthorized(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (isAuthorized === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-black">
        <div className="h-2 w-20 overflow-hidden rounded-full bg-white/10">
          <motion.div 
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="h-full w-full bg-yellow-500"
          />
        </div>
      </div>
    );
  }

  if (isAuthorized === false) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-black p-6 text-center">
        <div className="mb-6 rounded-full bg-red-500/10 p-6 ring-1 ring-red-500/20">
          <Lock size={40} className="text-red-500" />
        </div>
        <h2 className="text-2xl font-black uppercase tracking-tighter text-white">Acceso Restringido</h2>
        <p className="mt-2 text-sm text-white/40">Solo el equipo de Aura Business puede acceder a este registro.</p>
        <p className="mt-4 text-[10px] font-bold uppercase tracking-widest text-white/20">Redirigiendo...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white selection:bg-white/10">
      <div className="mx-auto max-w-4xl px-6 py-20">
        <button 
          onClick={() => navigate('/admin')}
          className="mb-12 flex items-center gap-2 text-white/40 transition-colors hover:text-white"
        >
          <ArrowLeft size={16} />
          <span className="text-[10px] font-bold uppercase tracking-widest">Volver al Panel</span>
        </button>

        <div className="mb-16 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 ring-1 ring-white/10">
              <History size={20} className="text-white/60" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight">Registro de Cambios</h1>
          </div>
          <p className="text-lg text-white/40 font-light">
            Sigue la evolución de Aura Business y las nuevas funcionalidades integradas.
          </p>
        </div>

        <div className="grid gap-8">
          <div className="prose prose-invert max-w-none">
            <div className="rounded-[2.5rem] border border-white/5 bg-white/[0.02] p-10 backdrop-blur-3xl">
              <div className="markdown-body">
                <Markdown>{content}</Markdown>
              </div>
            </div>
          </div>
        </div>

        {/* Resumen Visual para el Equipo */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-3xl border border-white/5 bg-white/[0.02] p-8">
            <Rocket size={24} className="mb-4 text-yellow-400" />
            <h3 className="mb-2 text-sm font-bold uppercase tracking-widest">Nuevas Funciones</h3>
            <p className="text-xs leading-relaxed text-white/40">Implementamos herramientas que ayudan a vender más y mejor.</p>
          </div>
          <div className="rounded-3xl border border-white/5 bg-white/[0.02] p-8">
            <Shield size={24} className="mb-4 text-blue-400" />
            <h3 className="mb-2 text-sm font-bold uppercase tracking-widest">Seguridad y Roles</h3>
            <p className="text-xs leading-relaxed text-white/40">Control total sobre quién accede a qué información.</p>
          </div>
          <div className="rounded-3xl border border-white/5 bg-white/[0.02] p-8">
            <Layout size={24} className="mb-4 text-purple-400" />
            <h3 className="mb-2 text-sm font-bold uppercase tracking-widest">Diseño Unificado</h3>
            <p className="text-xs leading-relaxed text-white/40">Una experiencia visual coherente en todos los puntos de contacto.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
